import Vue from 'vue'
import { themeApi } from '@/services/themeApi'
import { toRootDomain } from '@/utils/domain'
import themes from './themeRegistry'

// Reactive theme state shared app-wide as Vue.prototype.$theme. Created once
// in initTheme() from the bundled registry (+ localStorage warm cache), then
// refreshed in place by initThemeAsync() when the API theme arrives — the
// observable keeps $theme consumers (logo, lottie, game menu) re-rendering.
let themeState = null

// Hostname used for theme resolution. VUE_APP_THEME_DOMAIN lets local dev
// impersonate a real domain (both registry lookup and the API call use it).
function themeHostname() {
  return (
    process.env.VUE_APP_THEME_DOMAIN || toRootDomain(window.location.hostname)
  )
}

function cacheKey() {
  return `elz:theme:v1:${themeHostname()}`
}

function readCachedTheme() {
  try {
    const raw = localStorage.getItem(cacheKey())
    if (!raw) return null
    const parsed = JSON.parse(raw)
    // minimal shape check — anything older/broken falls back to the bundle
    if (parsed && parsed.name && parsed.primary) return parsed
    return null
  } catch (e) {
    return null
  }
}

function writeCachedTheme(theme) {
  try {
    localStorage.setItem(cacheKey(), JSON.stringify(theme))
  } catch (e) {
    // storage full/blocked — the API refresh still applies, just no warm cache
  }
}

/**
 * Convert hex color to RGB string for rgba() usage
 * @param {string} hex - Hex color (e.g., '#d4af37')
 * @returns {string} RGB values (e.g., '212, 175, 55')
 */
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (result) {
    return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(
      result[3],
      16
    )}`
  }
  return '255, 255, 255'
}

/**
 * Get theme configuration for current domain
 * @returns {object} Theme configuration object
 */
export function getThemeForDomain() {
  const hostname = themeHostname()
  return themes[hostname] || themes[themes.default]
}

/**
 * Apply theme to document by setting CSS custom properties
 * @param {object} theme - Theme configuration object
 */
export function applyTheme(theme) {
  const root = document.documentElement

  // Set primary colors
  root.style.setProperty('--theme-primary', theme.primary)
  root.style.setProperty('--theme-primary-rgb', hexToRgb(theme.primary))
  root.style.setProperty('--theme-primary-light', theme.primaryLight)

  // Set secondary colors
  root.style.setProperty('--theme-secondary', theme.secondary)
  root.style.setProperty('--theme-secondary-rgb', hexToRgb(theme.secondary))
  root.style.setProperty('--theme-secondary-light', theme.secondaryLight)

  // Set header, nav, card, modal, and sidebar backgrounds
  root.style.setProperty('--theme-header-bg', theme.headerBg)
  root.style.setProperty('--theme-nav-bg', theme.navBg)
  root.style.setProperty('--theme-card-bg', theme.cardBg)
  root.style.setProperty('--theme-modal-bg', theme.modalBg)
  root.style.setProperty('--theme-sidebar-bg', theme.sidebarBg)

  // Set input colors
  root.style.setProperty('--theme-input-bg', theme.inputBg)
  root.style.setProperty('--theme-input-border', theme.inputBorder)

  // Set gradient shortcuts
  root.style.setProperty(
    '--theme-gradient-primary',
    `linear-gradient(135deg, ${theme.primary}, ${theme.primaryLight})`
  )
  root.style.setProperty(
    '--theme-gradient-secondary',
    `linear-gradient(135deg, ${theme.secondary}, ${theme.secondaryLight})`
  )

  // Apply document title
  if (theme.title) {
    document.title = theme.title
  }

  // Apply favicon (falls back to logo when favicon is not provided)
  const faviconHref = theme.favicon || theme.logo
  if (faviconHref) {
    let link = document.querySelector("link[rel~='icon']")
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.head.appendChild(link)
    }
    link.href = faviconHref
  }

  // Apply body background
  if (theme.bodyBackground.type === 'image') {
    document.body.style.backgroundImage = `url(${theme.bodyBackground.value})`
    document.body.style.backgroundRepeat = 'no-repeat'
    document.body.style.backgroundPosition = 'top center'
    document.body.style.backgroundSize = 'cover'
    document.body.style.backgroundAttachment = 'fixed'
  } else {
    document.body.style.backgroundImage = 'none'
    document.body.style.backgroundColor = theme.bodyBackground.value
  }
}

/**
 * Initialize theme system - call this before Vue mount. Synchronous: paints
 * the bundled theme (or the last API theme cached in localStorage) instantly;
 * initThemeAsync() upgrades it from the API afterwards.
 * @returns {object} Reactive theme configuration (Vue.observable)
 */
export function initTheme() {
  const bundled = getThemeForDomain()
  const cached = readCachedTheme()
  // Merge over the bundle so a cached theme missing a newly-added field
  // never breaks consumers. All keys must exist here — Vue 2 observables
  // can't gain reactive keys later.
  themeState = Vue.observable({ ...bundled, ...(cached || {}) })
  applyTheme(themeState)
  return themeState
}

/**
 * Fetch the admin-managed theme from the API and apply it over the fallback.
 * Fire-and-forget: never throws, never blocks mount. On request failure the
 * current theme (bundled or cached) stays; when the API answers "no theme
 * configured" any cached theme is stale — drop it and restore the bundled one.
 * @param {object} httpInstance - Axios instance
 */
export async function initThemeAsync(httpInstance) {
  const apiTheme = await themeApi.fetchTheme(httpInstance, themeHostname())
  if (apiTheme === undefined || !themeState) return

  if (apiTheme === null) {
    try {
      localStorage.removeItem(cacheKey())
    } catch (e) {
      // storage blocked — nothing to clean
    }
    Object.assign(themeState, getThemeForDomain())
    applyTheme(themeState)
    return
  }

  const merged = { ...getThemeForDomain(), ...apiTheme }
  Object.assign(themeState, merged)
  applyTheme(themeState)
  writeCachedTheme(merged)
}
